import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Flex,
  Image,
  Badge,
  Textarea,
  Spinner,
  IconButton,
  useColorModeValue,
  Divider,
  Container,
  Grid,
  GridItem,
  Card,
  CardBody,
  useToast,
  Radio,
  RadioGroup,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input
} from '@chakra-ui/react';
import { 
  FaCheck, 
  FaTimes, 
  FaArrowLeft, 
  FaArrowRight, 
  FaStar, 
  FaRegStar, 
  FaComments,
  FaCamera,
  FaThumbtack,
  FaUserCheck
} from 'react-icons/fa';
import supabase from '../utils/supabaseUtils';
import './UserImagesUpload.css'; // Reuse existing styles
import AutoTemplateGenerator from './AutoTemplateGenerator';
import { useCESDK } from '../hooks/useCESDK';
import CreativeEditorSDK from '@cesdk/cesdk-js';

const ImageApprovalPage = () => {
  const { approvalId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [approval, setApproval] = useState(null);
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImage, setCurrentImage] = useState(null);
  const [decision, setDecision] = useState({});
  const [comments, setComments] = useState({});
  const [primaryImageId, setPrimaryImageId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false);
  
  // CESDK initialization
  const [cesdk, setCesdk] = useState(null);
  const cesdkContainerRef = useRef(null);
  const cesdkInitialized = useRef(false);
  
  // Initialize CESDK
  useEffect(() => {
    // Only initialize when needed for template generation
    if (showTemplateGenerator && !cesdkInitialized.current) {
      const initCESDK = async () => {
        try {
          console.log("Initializing CESDK for template generation...");
          
          // Create a hidden div for the CESDK instance
          const hiddenContainer = document.createElement('div');
          hiddenContainer.style.position = 'absolute';
          hiddenContainer.style.left = '-9999px';
          hiddenContainer.style.width = '1px';
          hiddenContainer.style.height = '1px';
          hiddenContainer.style.overflow = 'hidden';
          document.body.appendChild(hiddenContainer);
          
          // Initialize CESDK with minimal UI
          const instance = await CreativeEditorSDK.create(hiddenContainer, {
            license: import.meta.env.VITE_CESDK_LICENSE,
            baseURL: "/cesdk-assets",
            ui: { elements: { view: 'minimal' } },
            role: "Creator" 
          });
          
          console.log("CESDK initialized successfully for template generation");
          setCesdk(instance);
          cesdkInitialized.current = true;
          
          // Store reference for cleanup
          cesdkContainerRef.current = hiddenContainer;
        } catch (error) {
          console.error("Failed to initialize CESDK:", error);
          toast({
            title: "CESDK Initialization Error",
            description: "Could not initialize design engine. Templates may use fallback mode.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }
      };
      
      initCESDK();
    }
    
    // Cleanup function
    return () => {
      if (cesdk) {
        console.log("Cleaning up CESDK instance");
        try {
          cesdk.dispose();
          if (cesdkContainerRef.current) {
            document.body.removeChild(cesdkContainerRef.current);
          }
        } catch (e) {
          console.error("Error cleaning up CESDK:", e);
        }
      }
    };
  }, [showTemplateGenerator, toast]);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const headerBgColor = useColorModeValue('gray.700', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Fetch approval details and images
  useEffect(() => {
    const fetchApprovalDetails = async () => {
      try {
        setLoading(true);
        
        // Get approval record first
        const { data: approvalData, error: approvalError } = await supabase
          .from('image_approvals')
          .select('*')
          .eq('id', approvalId)
          .single();
        
        if (approvalError) throw approvalError;
        if (!approvalData) throw new Error('Approval not found');
        
        // Check if it's expired
        if (approvalData.expires_at && new Date(approvalData.expires_at) < new Date()) {
          throw new Error('This approval link has expired');
        }
        
        // Check if it's already completed
        if (approvalData.status === 'completed') {
          setCompleted(true);
        }
        
        setApproval(approvalData);
        
        // Get event details
        if (approvalData.event_id) {
          const { data: eventData } = await supabase
            .from('events')
            .select('*')
            .eq('id', approvalData.event_id)
            .single();
            
          if (eventData) {
            setEventDetails(eventData);
          }
        }
        
        // Get user details
        if (approvalData.user_id) {
          const { data: userData } = await supabase
            .rpc('get_user_by_id', { user_id: approvalData.user_id });
            
          if (userData) {
            setUserDetails(userData);
          }
        }
        
        // Get images for this approval
        const { data: imagesData, error: imagesError } = await supabase
          .from('user_uploads')
          .select('*')
          .eq('user_id', approvalData.user_id)
          .eq('approval_id', approvalId)
          .eq('status', 'active');
        
        if (imagesError) throw imagesError;
        
        if (imagesData && imagesData.length > 0) {
          setImages(imagesData);
          setCurrentImage(imagesData[0]);
          
          // Initialize decisions and comments objects
          const initialDecisions = {};
          const initialComments = {};
          
          imagesData.forEach(img => {
            initialDecisions[img.id] = img.approval_status || 'pending';
            initialComments[img.id] = img.approval_comment || '';
            
            // If there's a primary image already set, use it
            if (img.is_primary) {
              setPrimaryImageId(img.id);
            }
          });
          
          setDecision(initialDecisions);
          setComments(initialComments);
        } else {
          throw new Error('No images found for this approval');
        }
        
      } catch (error) {
        console.error('Error fetching approval details:', error);
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (approvalId) {
      fetchApprovalDetails();
    }
  }, [approvalId, toast]);
  
  // Handle image navigation
  const handleNextImage = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentImage(images[currentIndex + 1]);
    }
  };
  
  const handlePrevImage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setCurrentImage(images[currentIndex - 1]);
    }
  };
  
  // Handle decision change
  const handleDecisionChange = (imageId, value) => {
    setDecision({
      ...decision,
      [imageId]: value
    });
    
    // If an image is being approved and no primary image is set yet, set this as primary
    if (value === 'approved' && !primaryImageId) {
      setPrimaryImageId(imageId);
      console.log('Automatically set first approved image as primary:', imageId);
    }
    
    // Automatically move to the next image after a decision
    if (currentIndex < images.length - 1) {
      // Wait a brief moment before advancing to let the UI update with the decision
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setCurrentImage(images[currentIndex + 1]);
      }, 300); // 300ms delay to make it feel smoother
    } else {
      // If this is the last image, show a toast suggesting to submit
      if (allDecided()) {
        toast({
          title: "All images reviewed!",
          description: "You can now submit your decisions",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };
  
  // Handle comment change
  const handleCommentChange = (imageId, value) => {
    setComments({
      ...comments,
      [imageId]: value
    });
  };
  
  // Handle setting primary image
  const handleSetPrimary = (imageId) => {
    setPrimaryImageId(imageId);
    
    toast({
      title: 'Primary image set',
      description: 'This image will be used as the primary image',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };
  
  // Calculate progress
  const getProgress = () => {
    const total = images.length;
    const decided = Object.values(decision).filter(d => d === 'approved' || d === 'rejected').length;
    return (decided / total) * 100;
  };
  
  // Check if all images have decisions
  const allDecided = () => {
    return images.every(img => decision[img.id] === 'approved' || decision[img.id] === 'rejected');
  };
  
  // Generate summary stats
  const generateSummary = () => {
    const approvedCount = Object.values(decision).filter(d => d === 'approved').length;
    const rejectedCount = Object.values(decision).filter(d => d === 'rejected').length;
    const pendingCount = Object.values(decision).filter(d => d !== 'approved' && d !== 'rejected').length;
    
    return {
      total: images.length,
      approved: approvedCount,
      rejected: rejectedCount,
      pending: pendingCount,
      primary: primaryImageId,
      progress: getProgress(),
      completed_at: new Date().toISOString()
    };
  };
  
  // Direct update method as fallback when RPC fails
  const fallbackUpdateApproval = async () => {
    try {
      const { error } = await supabase
        .from('image_approvals')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          summary: generateSummary(),
          primary_image_id: primaryImageId
        })
        .eq('id', approvalId);
      
      return { success: !error, error };
    } catch (err) {
      console.error("Fallback update failed:", err);
      return { success: false, error: err };
    }
  };
  
  // Fallback method to update image status
  const fallbackUpdateImage = async (imageId, status, comment, isPrimary) => {
    try {
      // First try a direct update with minimal fields
      const { error } = await supabase
        .from('user_uploads')
        .update({
          approval_status: status,
          approval_comment: comment,
          is_primary: isPrimary
        })
        .eq('id', imageId);
      
      return { success: !error, error };
    } catch (err) {
      console.error("Fallback image update failed:", err);
      return { success: false, error: err };
    }
  };
  
  // Handle submission of all decisions
  const handleSubmit = async () => {
    try {
      // Ensure a primary image is selected if any images are approved
      const approvedImages = images.filter(img => decision[img.id] === 'approved');
      if (approvedImages.length > 0 && !primaryImageId) {
        // Set the first approved image as primary if none was selected
        setPrimaryImageId(approvedImages[0].id);
        console.log('Auto-setting primary image on submission to:', approvedImages[0].id);
      }
      
      if (!primaryImageId && Object.values(decision).some(d => d === 'approved')) {
        toast({
          title: 'Primary image required',
          description: 'Please select a primary image from your approved images',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      setSubmitting(true);
      
      // Track failed updates to handle them gracefully
      const failedUpdates = [];
      
      // Update all images with decisions and comments using the secure function
      for (const image of images) {
        const isPrimary = image.id === primaryImageId;
        
        try {
          // Use the RPC function instead of direct update
          const { data, error } = await supabase.rpc('update_image_approval_status', {
            image_id_param: image.id,
            approval_status_param: decision[image.id] || 'pending',
            approval_comment_param: comments[image.id] || '',
            is_primary_param: isPrimary
          });
            
          if (error) {
            console.error(`Error updating image ${image.id}:`, error);
            // Try fallback method
            console.log("Attempting fallback update for image:", image.id);
            const fallbackResult = await fallbackUpdateImage(
              image.id, 
              decision[image.id] || 'pending', 
              comments[image.id] || '', 
              isPrimary
            );
            
            if (!fallbackResult.success) {
              failedUpdates.push({ id: image.id, error });
            }
          }
        } catch (err) {
          console.error(`Exception updating image ${image.id}:`, err);
          
          // Try fallback method here too
          const fallbackResult = await fallbackUpdateImage(
            image.id, 
            decision[image.id] || 'pending', 
            comments[image.id] || '', 
            isPrimary
          );
          
          if (!fallbackResult.success) {
            failedUpdates.push({ id: image.id, error: err });
          }
        }
      }
      
      // If all updates failed, but we'll still try to continue
      const allUpdatesFailed = failedUpdates.length === images.length;
      if (allUpdatesFailed) {
        console.error(`All ${images.length} image updates failed.`);
      }
      
      let approvalUpdateSucceeded = false;
      
      try {
        // Update the approval status
        const { error: approvalError } = await supabase.rpc('complete_approval', {
          approval_id_param: approvalId,
          summary_param: generateSummary(),
          primary_image_id_param: primaryImageId
        });
          
        if (approvalError) {
          console.error("Error completing approval:", approvalError);
          // Try fallback method
          console.log("Attempting fallback for approval completion");
          const fallbackResult = await fallbackUpdateApproval();
          approvalUpdateSucceeded = fallbackResult.success;
        } else {
          approvalUpdateSucceeded = true;
          
          // If the RPC call was successful, we still need to update the primary_image_id
          // as that might not be handled by the RPC function
          try {
            const { error: primaryUpdateError } = await supabase
              .from('image_approvals')
              .update({ primary_image_id: primaryImageId })
              .eq('id', approvalId);
              
            if (primaryUpdateError) {
              console.warn("Primary image ID update failed:", primaryUpdateError);
            } else {
              console.log("Updated primary_image_id successfully:", primaryImageId);
            }
          } catch (primaryError) {
            console.warn("Error updating primary_image_id:", primaryError);
          }
        }
      } catch (approvalErr) {
        console.error("Exception completing approval:", approvalErr);
        // Try fallback method
        console.log("Attempting fallback for approval completion after exception");
        const fallbackResult = await fallbackUpdateApproval();
        approvalUpdateSucceeded = fallbackResult.success;
      }
      
      // Show summary regardless of DB success/failure
      setSummary(generateSummary());
      setCompleted(true);
      
      // Notify with appropriate message based on failures
      if (allUpdatesFailed && !approvalUpdateSucceeded) {
        toast({
          title: 'Local review completed',
          description: 'All database operations failed, but your review has been recorded locally. Please contact support.',
          status: 'error',
          duration: 7000,
          isClosable: true,
        });
      } else if (failedUpdates.length > 0) {
        toast({
          title: 'Review partially submitted',
          description: `Your review was processed, but ${failedUpdates.length} of ${images.length} image updates failed. The system administrator will be notified.`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Review submitted',
          description: 'Your image review has been successfully submitted',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      
      // Activate the template generator if at least one image was approved
      if (Object.values(decision).some(d => d === 'approved')) {
        // Wait a short time before showing the generator to allow UI updates
        setTimeout(() => {
          setShowTemplateGenerator(true);
        }, 1000);
      }
      
      // Open summary modal even if there were errors
      onOpen();
      
    } catch (error) {
      console.error('Error submitting review:', error);
      
      // Even if we have a catastrophic error, still try to show the summary
      try {
        setSummary(generateSummary());
        setCompleted(true);
        onOpen();
      } catch (e) {
        console.error("Could not display summary due to error:", e);
      }
      
      toast({
        title: 'Error',
        description: 'There was an error submitting your review: ' + (error.message || 'Unknown error'),
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Flex justify="center" align="center" height="100vh" width="100%">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text fontSize="xl">Loading images for approval...</Text>
        </VStack>
      </Flex>
    );
  }
  
  if (completed && !currentImage) {
    return (
      <Container maxW="container.lg" py={10}>
        <Alert
          status="success"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
          borderRadius="md"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Review Completed
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            This image review has already been completed.
          </AlertDescription>
        </Alert>
      </Container>
    );
  }
  
  return (
    <Box minH="100vh" bg="gray.50">
      {/* Show the template generator when completed and activated */}
      {showTemplateGenerator && (
        <Container maxW="container.md" py={6}>
          <Box 
            bg="white" 
            borderRadius="md" 
            p={6} 
            boxShadow="md"
            mb={6}
          >
            <AutoTemplateGenerator approvalId={approvalId} cesdk={cesdk} />
          </Box>
        </Container>
      )}
      
      {/* Header */}
      <Box bg={headerBgColor} color="white" py={4} px={6} mb={6}>
        <Container maxW="container.xl">
          <Flex justify="space-between" display="none" align="center" flexDirection={{ base: 'column', sm: 'row' }} gap={{ base: 2, sm: 0 }}>
            <VStack align={{ base: 'center', sm: 'start' }} spacing={0}>
              <Heading size="md">Image Approval</Heading>
              {eventDetails && (
                <Text fontSize="sm" textAlign={{ base: 'center', sm: 'left' }}>
                  {eventDetails.name} • {eventDetails.city}, {eventDetails.state}
                </Text>
              )}
            </VStack>
            
            {userDetails && (
              <HStack mt={{ base: 2, sm: 0 }}>
                <FaUserCheck />
                <Text>{userDetails.first_name} {userDetails.last_name}</Text>
              </HStack>
            )}
          </Flex>
        </Container>
      </Box>
      
      {/* Only show the approval interface if we're not showing the template generator */}
      {!showTemplateGenerator && (
        <Container maxW="container.xl" pb={10} px={{ base: 2, md: 6 }}>
          <Card mb={6} borderRadius="lg" overflow="hidden">
            <CardBody p={0}>
              <Grid templateColumns={{ base: "1fr", md: "3fr 1fr" }} gap={0}>
                {/* Main Image Display */}
                <GridItem position="relative" bg="white">
                  {currentImage && (
                    <Box position="relative">
                      <Image
                        src={currentImage.file_url}
                        alt="Image for approval"
                        w="100%"
                        maxH={{ base: "50vh", md: "75vh" }}
                        objectFit="contain"
                        bg="white"
                      />
                      
                      {/* Navigation buttons */}
                      <HStack 
                        position="absolute" 
                        bottom="10px" 
                        left="50%" 
                        transform="translateX(-50%)"
                        spacing={4}
                      >
                        <IconButton
                          icon={<FaArrowLeft />}
                          aria-label="Previous image"
                          onClick={handlePrevImage}
                          isDisabled={currentIndex === 0}
                          colorScheme="gray"
                          variant="solid"
                          isRound
                        />
                        <Text color="white" bg="blackAlpha.700" px={3} py={1} borderRadius="md">
                          {currentIndex + 1} of {images.length}
                        </Text>
                        <IconButton
                          icon={<FaArrowRight />}
                          aria-label="Next image"
                          onClick={handleNextImage}
                          isDisabled={currentIndex === images.length - 1}
                          colorScheme="gray"
                          variant="solid"
                          isRound
                        />
                      </HStack>
                    </Box>
                  )}
                </GridItem>
                
                {/* Decision Panel */}
                <GridItem bg="white" p={{ base: 3, sm: 6 }} borderLeft={{ md: '1px solid' }} borderColor="gray.200" borderTop={{ base: '1px solid', md: 'none' }} borderTopColor="gray.200">
                  <VStack align="stretch" spacing={6}>
                    <Box>
                      <Heading size="md" mb={2}>Image {currentIndex + 1}</Heading>
                      <HStack>
                        <Badge colorScheme={
                          decision[currentImage?.id] === 'approved' ? 'green' :
                          decision[currentImage?.id] === 'rejected' ? 'red' : 'gray'
                        }>
                          {decision[currentImage?.id]?.toUpperCase() || 'PENDING'}
                        </Badge>
                        
                        {primaryImageId === currentImage?.id && (
                          <Badge colorScheme="yellow">PRIMARY</Badge>
                        )}
                      </HStack>
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Text fontWeight="bold" mb={2}>Your Decision</Text>
                      <RadioGroup 
                        onChange={(value) => handleDecisionChange(currentImage?.id, value)} 
                        value={decision[currentImage?.id] || 'pending'}
                      >
                        <VStack align="stretch" spacing={3}>
                          <Radio value="approved" colorScheme="green">
                            <HStack>
                              <FaCheck color="green" />
                              <Text>Approve this image</Text>
                            </HStack>
                          </Radio>
                          <Radio value="rejected" colorScheme="red">
                            <HStack>
                              <FaTimes color="red" />
                              <Text>Reject this image</Text>
                            </HStack>
                          </Radio>
                        </VStack>
                      </RadioGroup>
                    </Box>
                    
                    <Box>
                      <Text fontWeight="bold" mb={2}>Comments</Text>
                      <Textarea
                        placeholder="Add any comments about this image..."
                        value={comments[currentImage?.id] || ''}
                        onChange={(e) => handleCommentChange(currentImage?.id, e.target.value)}
                        resize="vertical"
                        rows={4}
                      />
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Text fontWeight="bold" mb={2}>Overall Progress</Text>
                      <Progress value={getProgress()} colorScheme="gray" borderRadius="md" mb={2} />
                      <Text fontSize="sm" color="gray.600">
                        {Object.values(decision).filter(d => d === 'approved' || d === 'rejected').length} of {images.length} images reviewed
                      </Text>
                    </Box>
                    
                    <Button
                      colorScheme="gray"
                      size="lg"
                      width="100%"
                      onClick={handleSubmit}
                      isDisabled={!allDecided() || submitting}
                      isLoading={submitting}
                      loadingText="Submitting..."
                    >
                      {allDecided() ? 'Submit All Decisions' : 'Review All Images to Continue'}
                    </Button>
                  </VStack>
                </GridItem>
              </Grid>
            </CardBody>
          </Card>
          
          {/* Thumbnails Navigation */}
          <Grid
            templateColumns="repeat(auto-fill, minmax(80px, 1fr))"
            gap={{ base: 2, md: 3 }}
            mb={6}
          >
            {images.map((image, index) => (
              <GridItem 
                key={image.id} 
                onClick={() => {
                  setCurrentIndex(index);
                  setCurrentImage(image);
                }}
                cursor="pointer"
                position="relative"
              >
                <Box
                  borderWidth="2px"
                  borderColor={currentImage?.id === image.id ? 'gray.500' : 'transparent'}
                  borderRadius="md"
                  overflow="hidden"
                  position="relative"
                  _hover={{ transform: 'scale(1.05)', transition: 'transform 0.2s' }}
                  bg="white"
                >
                  <Image
                    src={image.file_url}
                    alt={`Thumbnail ${index + 1}`}
                    objectFit="cover"
                    h={{ base: "60px", md: "70px" }}
                    w="100%"
                    bg="white"
                  />
                  
                  {/* Decision indicator */}
                  <Box
                    position="absolute"
                    bottom="0"
                    left="0"
                    right="0"
                    py={1}
                    bg={
                      decision[image.id] === 'approved' ? 'green.500' :
                      decision[image.id] === 'rejected' ? 'red.500' : 'gray.500'
                    }
                    opacity={0.8}
                  >
                    <Text 
                      fontSize="xs" 
                      color="white" 
                      textAlign="center"
                      fontWeight="bold"
                    >
                      {index + 1}
                    </Text>
                  </Box>
                  
                  {/* Primary indicator */}
                  {primaryImageId === image.id && (
                    <Box position="absolute" top="2px" right="2px">
                      <FaStar color="gold" />
                    </Box>
                  )}
                </Box>
              </GridItem>
            ))}
          </Grid>
          
          {/* Mobile Navigation */}
          <Flex display={{ base: 'flex', md: 'none' }} justify="space-between" mb={6}>
            <Button
              leftIcon={<FaArrowLeft />}
              onClick={handlePrevImage}
              isDisabled={currentIndex === 0}
              colorScheme="gray"
              size="sm"
              flex={1}
              mr={2}
            >
              Prev
            </Button>
            <Button
              rightIcon={<FaArrowRight />}
              onClick={handleNextImage}
              isDisabled={currentIndex === images.length - 1}
              colorScheme="gray"
              size="sm"
              flex={1}
              ml={2}
            >
              Next
            </Button>
          </Flex>
          
          {/* Help text */}
          <Text fontSize="sm" color="gray.500" textAlign="center" fontWeight="medium">
            Please review all images and make a decision. You'll need to select a primary image from your approved images.
          </Text>
        </Container>
      )}
      
      {/* Summary Modal */}
      
    </Box>
  );
};

export default ImageApprovalPage; 